import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cn from 'classnames';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { paginationMessages, sharedMessages } from '@app/translations';
import { PropsWithClassName } from '@app/constants';

import './PreviousNextPagination.scss';

interface PreviousNextPaginationProps extends PropsWithClassName {
  pageNumber: number;
  totalCount: number;
  loadingPageDirection?: PreviousNextNavigationDirection;
  onPrevious: () => void;
  onNext: () => void;
}

export type PreviousNextNavigationDirection = 'previous' | 'next';

const cnBem = createBemBlockBuilder(['previousNextPagination']);

export const PreviousNextPagination: FC<
  PropsWithClassName<PreviousNextPaginationProps>
> = ({
  pageNumber,
  totalCount,
  className,
  loadingPageDirection,
  onPrevious,
  onNext,
}) => {
  const { $t } = useIntl();

  const isPreviousButtonDisabled =
    pageNumber === 1 || loadingPageDirection === 'previous';
  const isNextButtonDisabled =
    pageNumber === totalCount || loadingPageDirection === 'next';

  return (
    <nav
      aria-label={$t(paginationMessages.pagination)}
      className={cn(cnBem(), className)}
    >
      <button
        className="eb-button eb-button--link"
        disabled={isPreviousButtonDisabled}
        type="button"
        onClick={onPrevious}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        <FormattedMessage {...sharedMessages.previous} />
      </button>
      <FormattedMessage
        {...sharedMessages.of}
        values={{
          number: pageNumber,
          total: totalCount,
        }}
      />
      <button
        className="eb-button eb-button--link"
        disabled={isNextButtonDisabled}
        type="button"
        onClick={onNext}
      >
        <FormattedMessage {...sharedMessages.next} />
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </nav>
  );
};
